import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  InputAdornment,
  LinearProgress,
  useTheme,
} from '@mui/material';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import TuneIcon from '@mui/icons-material/Tune';

import StyledTextField from '@shared/components/StyledTextField';

const HotelSearchHeader = ({
  nameFilter,
  setNameFilter,
  quantityFiltered,
  citySelected,
  handleClickOpen,
  allFilteredHotels = [],
  isLoadingResults,
  showProgress,
  setShowProgress,
}) => {
  const totalResults = quantityFiltered ?? allFilteredHotels.length;

  const theme = useTheme();

  const [progress, setProgress] = useState(0);
  const [localNameFilter, setLocalNameFilter] = useState(nameFilter);

  useEffect(() => {
    const progressRef = { current: 0 };
    let animationFrameId = null;
    let hideTimeoutId = null;

    const animate = () => {
      setProgress(prev => {
        const target = isLoadingResults ? 95 : 100;
        const step = Math.max((target - prev) * 0.1, 0.5);
        const next = prev + step;
        progressRef.current = next;

        if (next < target) {
          animationFrameId = requestAnimationFrame(animate);
        } else if (!isLoadingResults && next >= 100) {
          hideTimeoutId = setTimeout(() => setShowProgress(false), 500);
        }

        return next >= target ? target : next;
      });
    };

    setShowProgress(true);
    animate();

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (hideTimeoutId) clearTimeout(hideTimeoutId);
    };
  }, [isLoadingResults]);

  return (
    <Grid
      container
      direction="column"
      rowSpacing={{ xs: 1, sm: 2 }}
      id="GRIDHEADER"
    >
      {/* Resultados */}
      <Grid item sx={{ mb: { xs: 1, sm: 0 } }}>
        <Typography
          sx={{
            color: 'black',
            fontWeight: 600,
            fontSize: 16,
            mt: { xs: 2, sm: 2 },
            pb: { xs: showProgress ? 0 : '20px' },
            lineHeight: { xs: 1, sm: 1.57 },
          }}
        >
          Encontramos {totalResults} resultados en {citySelected.label}
        </Typography>
      </Grid>

      {/* Barra de progreso */}
      {showProgress && (
        <Grid item sx={{ width: '100%', mb: { xs: 1, sm: 2 } }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: '15px',
              mb: 2,
              '& .MuiLinearProgress-barColorPrimary': {
                backgroundColor: theme.primary,
              },
              '& .MuiLinearProgress-barColorSecondary': {
                backgroundColor: theme.primary + '80',
              },
            }}
          />
        </Grid>
      )}

      {/* Filtros */}
      <Grid
        container
        columnSpacing={2}
        rowSpacing={{ xs: 2 }}
        alignItems="center"
        id="GRIDFILTROS"
      >
        {/* Input */}
        <Grid
          item
          xs={12}
          sm={10}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <StyledTextField
            label="Buscá tu Hotel"
            placeholder="Ingresa al menos 3 letras"
            fullWidth
            value={localNameFilter || nameFilter}
            onChange={e => {
              setLocalNameFilter(e.target.value);
              setNameFilter(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <RoomServiceIcon sx={{ color: '#000' }} />
                </InputAdornment>
              ),
            }}
            textFieldSx={{ minHeight: '55px' }}
            height="55px"
            minHeight="55px"
            disabled={showProgress}
          />
        </Grid>

        {/* Botón */}
        <Grid
          item
          xs={12}
          sm={2}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Button
            variant="contained"
            onClick={handleClickOpen}
            disabled={showProgress}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontSize: '18px',
              height: '55px',
              fontWeight: 500,
              borderRadius: '8px',
              color: theme.tertiary,
              bgcolor: '#fff',
              border: `1px solid ${theme.tertiary}`,
              '&:hover': {
                bgcolor: '#f6e6e6',
              },
            }}
          >
            <TuneIcon sx={{ mr: '5px' }} fontSize="small" /> Filtros
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default HotelSearchHeader;
